import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { SubmitHandler } from 'react-hook-form/dist/types'
import { toast } from 'react-toastify'

import {
  Button,
  Dialog,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Typography } from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { Coupon } from 'features/coupons/types/coupon'
import useTags from 'hooks/useTags'
import { Products } from 'models/Plans'
import { ProductLink } from 'models/ProductLink'
import { useRouter } from 'next/router'
import {
  formatSharedChargeData,
  getCreditCardPaymentOptionsText,
  companyDeadlineDefaults,
  sharedInitialFormValues,
  setSharedFormData
} from 'templates/Charge/sharedCharge.utils'
import { formatedMessageError } from 'templates/Customers/Modals/BodyModalNewChargeOrRecurrence/BodyModalNewChargeOrRecurrence.utils'
import CreatePaymentLinkForm from 'templates/PaymentLink/components/Form'
import CreatePaymentLinkFormInterface, {
  PaymentLink
} from 'templates/PaymentLink/interface'
import createPaymentLinkSchema from 'templates/PaymentLink/schema'
import { resolveSimplifiedCheckoutPayload } from 'templates/PaymentLink/simplifiedCheckout'
import {
  createExtraFieldsInitialValue,
  createFormValuesFromPaymentLinkV3,
  convertProductPropertiesToCamelCase
} from 'templates/PaymentLink/utils'
import FormProducts from 'templates/v2/Products/Form/FormProducts'
import {
  displaySettingsPayload,
  FormSection,
  layoutProblems,
  normalizeSections
} from 'templates/v2/RegistrationLink/components/FormLayout/formLayout.utils'
import { v4 as uuidv4 } from 'uuid'

import { useInCompanyForm, useSessionCookies } from 'components/CompanySelector'
import { ExtraFieldItem } from 'components/ExtraFieldsManager'
import IconContainer from 'components/IconContainer/IconContainer'
import Skeleton from 'components/Modal/ModalDetailsExpenses/components/Skeleton/Skeleton'
import ModalPaymentLinkDetail from 'components/Modal/ModalPaymentLinkDetail'
import ModalPaymentLinkPreview from 'components/Modal/ModalPaymentLinkPreview'
import { PaymentsTypeRef } from 'components/PaymentsType'
import TagsPopover from 'components/TagsPopover/TagsPopover'

import { formatCents } from 'utils'
import { getPortalBaseUrl } from 'utils/domain-urls'
import { FREQUENCY_NAME_TO_ENUM, presetToPair } from 'utils/frequency'
import { prepareFormDataWithTags } from 'utils/tags'
import { toastFirstError } from 'utils/yupResolverWithToast'

import { theme } from 'styles/theme'

const initialFormValues = {
  ...sharedInitialFormValues,
  // Campos específicos do payment link
  customDomain: '',
  customer: { label: '', value: '' },
  customerUuid: '',
  productUuid: 'initial',
  customerType: 'new',
  customMessage: '',
  customUrl: '',
  hasCustomDomain: false,
  hasCustomMessage: false,
  hasCustomUrl: false,
  hasExtraFields: false,
  hasCustomerGroups: false,
  title: '',
  sendWelcomeEmail: false,
  simplifiedCheckout: false,
  canChangeCheckoutQuantity: false,
  defaultQuantity: 1,
  groupsValue: [],
  discounts: [],
  identifyInternal: [],
  group: { label: '', value: '' },
  groupUuid: '',
  recieverType: 'customer',
  // Sobrescrever valores específicos do payment link
  chargeType: 0, // 0 = cobrança única por padrão
  dueType: 'immediate_charge',
  expirationField: '60',
  emails_to_notify: [],
  items: [
    {
      price: 0,
      quantity: 1,
      total: 0,
      product: undefined,
      description: '',
      typedValue: '',
      uuid: uuidv4(),
      enable_customer_select_quantity: false,
      minimum_quantity: 1
    }
  ],
  // Campos de comportamento de documento existente e contatos
  // SC-10380: default é update_customer (2) — na renovação o link deve ATUALIZAR
  // o cadastro existente em vez de criar customer/recorrência paralelos.
  existing_document_behavior: 2,
  contacts_enabled: false,
  contacts: [],
  link_type: 'BOTH',
  consider_active_on_registration: true,
  cancel_if_first_billing_unpaid: false,
  hasContract: false,
  contractModelUuid: null,
  contractBillingBehavior: 'contract_and_billing'
} as CreatePaymentLinkFormInterface

const extraFieldsDefaultValue: ExtraFieldItem[] = []

interface ModalNewPaymentLinkProps {
  isOpen: boolean
  handleClose: (
    refresh?: boolean,
    newPaymentLink?: any,
    reopenDetails?: boolean
  ) => void
  paymentLink?: PaymentLink
  isEdit?: boolean
  isDuplication?: boolean
  product?: Products
  productUuid?: string
  handleCloseCreatePaymentLink?: () => void
  // SC-10999: seletor "Empresa" da criação na visão multi-empresas
  companySelector?: ReactNode
}

const ModalNewPaymentLink = ({
  isOpen,
  handleClose,
  paymentLink,
  isEdit = false,
  isDuplication = false,
  product,
  productUuid,
  handleCloseCreatePaymentLink,
  companySelector
}: ModalNewPaymentLinkProps) => {
  const inCompanyForm = useInCompanyForm()
  const sessionCookies = useSessionCookies()
  const {
    control,
    watch,
    setValue,
    setError,
    trigger,
    handleSubmit,
    register,
    reset,
    formState,
    clearErrors
  } = useForm<CreatePaymentLinkFormInterface>({
    defaultValues: {
      ...initialFormValues,
      // SC-10999: criado para outra empresa do grupo, valem os prazos dela
      ...(inCompanyForm && companyDeadlineDefaults(sessionCookies))
    },
    resolver: yupResolver(createPaymentLinkSchema),
    context: { isProductOrLink: true, isLink: true },
    mode: 'onChange'
  })

  // ✅ useFieldArray para descontos (após useForm)
  const { replace: replaceDiscounts } = useFieldArray({
    control,
    name: 'discounts'
    // ✅ Removido defaultValue - será inicializado pelos useEffects
  })

  // useFieldArray para gerenciar emails para notificar
  const {
    fields: emailsFields,
    append: appendEmail,
    remove: removeEmail
  } = useFieldArray({
    control,
    name: 'emails_to_notify'
  })

  // useFieldArray para gerenciar contatos
  const {
    fields: contactsFields,
    append: appendContact,
    remove: removeContact
  } = useFieldArray({
    control,
    name: 'contacts'
  })

  const { query } = useRouter()
  const { user } = useAuthContext()
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const paymentsTypeRef = useRef<PaymentsTypeRef>(null)
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    dataForm: CreatePaymentLinkFormInterface | null
  }>({
    open: false,
    dataForm: null
  })

  const [isOpenProductModal, setIsOpenProductModal] = useState(false)
  const [isOpenPreview, setIsOpenPreview] = useState(false)

  const [createProductLink, setCreateProductLink] =
    useState<ProductLink | null>(null)

  const [fetchedProduct, setFetchedProduct] = useState<Products | null>(null)
  const [loadingProduct, setLoadingProduct] = useState(false)
  const [contractModels, setContractModels] = useState<
    Array<{ label: string; value: string }>
  >([])

  const contractValidationDefault = {
    loading: false,
    blocked: false,
    blockReason: '',
    missingCustomFields: [] as Array<{ uuid: string; title: string }>
  }
  const [contractValidation, setContractValidation] = useState<{
    loading: boolean
    blocked: boolean
    blockReason: string
    missingCustomFields: Array<{ uuid: string; title: string }>
  }>(contractValidationDefault)

  const [extraFields, setExtraFields] = useState<ExtraFieldItem[]>(
    paymentLink &&
      ((paymentLink as any).custom_field_configs?.length > 0 ||
        (paymentLink.extra_fields?.length || 0) > 0)
      ? createExtraFieldsInitialValue(paymentLink)
      : extraFieldsDefaultValue
  )
  // SC-11487: seções do formulário do checkout (mesmo editor do Link de Cadastro)
  const [sections, setSections] = useState<FormSection[]>(() =>
    normalizeSections((paymentLink as any)?.link_fields?.sections)
  )

  // Coupons state
  const [couponsEnabled, setCouponsEnabled] = useState(false)
  const [associatedCoupons, setAssociatedCoupons] = useState<Coupon[]>([])

  // Estados necessários para integração com BodyModalNewChargeOrRecurrence
  const [isUnique, setIsUnique] = useState(true)
  const [isCharge, setIsCharge] = useState(false)

  const [hasInitialProduct, setHasInitialProduct] = useState<boolean>(false)

  // Determina qual produto usar: o passado como prop ou o buscado
  const currentProduct = product || fetchedProduct

  const handleProductRemoved = (keepConfig = false) => {
    setHasInitialProduct(false)
    if (keepConfig) {
      setValue('items.0.price', 0)
      setValue('items.0.quantity', 1)
      setValue('items.0.total', 0)
      setValue('items.0.description', '')
      setValue('items.0.product', undefined)
      setValue('title', '')
      setValue('productUuid', 'initial')
      return
    }

    reset(initialFormValues)
    setExtraFields([])
    setSections([])
    replaceDiscounts([])
    setIsUnique(true)
  }

  const getContractModels = useCallback(async () => {
    try {
      const res = await DataService({
        url: '/v1/contract_models/',
        type: 'GET'
      })
      if (res.status === 200) {
        setContractModels(
          res.data.records.map((r: any) => ({ label: r.title, value: r.uuid }))
        )
      }
    } catch (error) {
      console.error('Erro ao carregar modelos de contrato:', error)
    }
  }, [])

  // Validate contract model compatibility
  const validateContractModel = useCallback(
    async (
      contractModelUuid: string | null,
      overrideExtraFields?: typeof extraFields
    ) => {
      if (!contractModelUuid) {
        setContractValidation(contractValidationDefault)
        return
      }

      setContractValidation((prev) => ({ ...prev, loading: true }))

      try {
        const response = await DataService({
          url: `/v1/contract_models/${contractModelUuid}/required_fields`,
          type: 'GET'
        })

        if (response.status !== 200) {
          setContractValidation(contractValidationDefault)
          return
        }

        const { custom_fields, billing_fields } = response.data

        // 1. Check billing variables - only block if behavior is billing_only
        const currentBehavior =
          watch('contractBillingBehavior') || 'contract_and_billing'
        if (
          billing_fields &&
          billing_fields.length > 0 &&
          currentBehavior !== 'contract_and_billing' &&
          currentBehavior !== 'contract_before_billing'
        ) {
          setContractValidation({
            loading: false,
            blocked: true,
            blockReason:
              'Este modelo possui variáveis de cobrança/recorrência que não são compatíveis com o comportamento de cobrança selecionado.',
            missingCustomFields: []
          })
          return
        }

        // 2. Block if nickname variable exists
        const { customer_fields } = response.data
        const hasNickname = customer_fields?.some(
          (f: any) => f.field === 'nickname'
        )
        if (hasNickname) {
          setContractValidation({
            loading: false,
            blocked: true,
            blockReason:
              'Este modelo possui a variável "Apelido do cliente" que não está disponível no Link de Contratação.',
            missingCustomFields: []
          })
          return
        }

        // 3. Standard fields are always collected in checkout - no check needed

        // 4. Check custom fields
        const effectiveExtraFields = overrideExtraFields ?? extraFields
        const currentExtraUuids = effectiveExtraFields.map((ef) => ef.uuid)
        const missingCustom = (custom_fields || [])
          .filter((f: any) => !currentExtraUuids.includes(f.uuid as string))
          .map((f: any) => ({ uuid: f.uuid, title: f.title }))

        setContractValidation({
          loading: false,
          blocked: false,
          blockReason: '',
          missingCustomFields: missingCustom
        })
      } catch (error) {
        console.error('Erro ao validar modelo de contrato:', error)
        setContractValidation(contractValidationDefault)
      }
    },
    [watch, extraFields]
  )

  // Add a missing custom field from the contract validation alert
  const handleAddContractCustomField = useCallback(
    (field: { uuid: string; title: string }) => {
      setValue('hasExtraFields', true)
      setExtraFields((prev) => [
        ...prev,
        {
          uuid: field.uuid,
          name: field.title,
          field_type: 'short_text',
          required: true
        }
      ])
      setContractValidation((prev) => ({
        ...prev,
        missingCustomFields: prev.missingCustomFields.filter(
          (f) => f.uuid !== field.uuid
        )
      }))
    },
    [setValue]
  )

  // Add all missing custom fields at once
  const handleAddAllContractCustomFields = useCallback(() => {
    setValue('hasExtraFields', true)
    contractValidation.missingCustomFields.forEach((f) => {
      setExtraFields((prev) => [
        ...prev,
        {
          uuid: f.uuid,
          name: f.title,
          field_type: 'short_text',
          required: true
        }
      ])
    })
    setContractValidation((prev) => ({
      ...prev,
      missingCustomFields: []
    }))
  }, [contractValidation.missingCustomFields, setValue])

  const hasContractIssues =
    contractValidation.blocked ||
    contractValidation.missingCustomFields.length > 0

  // Hook para gerenciar tags
  const { tags: availableTags } = useTags()

  const customerGroups =
    paymentLink && paymentLink.customer_groups?.length
      ? paymentLink.customer_groups.map(({ uuid, name }) => ({
          label: name,
          value: uuid
        }))
      : []

  // Sincronizar chargeType com isUnique (bidirecional) - igual aos produtos
  useEffect(() => {
    const chargeType = watch('chargeType')
    setIsUnique(chargeType === 0) // 0 = única, 1 = recorrente
  }, [watch('chargeType')])

  useEffect(() => {
    setValue('chargeType', isUnique ? 0 : 1)
  }, [isUnique, setValue])

  const handleOpenNewProductModal = () => {
    setIsOpenProductModal(!isOpenProductModal)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    // Se foi uma criação ou edição bem-sucedida, passa true para indicar refresh
    // e o novo payment link se foi uma duplicação
    handleClose(true, isDuplication ? createProductLink : undefined, false)
  }

  const handleCloseModal = () => {
    // Resetar o formulário quando o modal for fechado (exceto no modo de edição)
    if (!isEdit) {
      reset(initialFormValues)
      setExtraFields(extraFieldsDefaultValue)
      setSections([])
      // ✅ Usar replaceDiscounts em vez de setAddFieldDiscount
      replaceDiscounts([])
      setIsUnique(true)
    }
    if (productUuid) {
      handleCloseCreatePaymentLink && handleCloseCreatePaymentLink()
    }
    // Limpar o createProductLink quando o modal fechar completamente
    setCreateProductLink(null)
    setShowDetailsModal(false)
    setDialogOpen(false)
    setContractValidation(contractValidationDefault)
    handleClose(false, undefined, false)
  }

  const openPaymentLink = () => {
    window.open(
      `${getPortalBaseUrl()}/checkout/${createProductLink?.uuid}`,
      '_blank'
    )
  }

  const openDetailsModal = () => {
    setDialogOpen(false) // Fechar o dialog de sucesso

    if (isEdit) {
      // Para edição: fechar o modal de edição (o modal de detalhes já está aberto e será atualizado)
      handleClose(true, createProductLink, true) // true indica que deve reabrir o modal de detalhes
    } else {
      // Para criação: usar setTimeout para garantir que o modal de detalhes abra após o fechamento do dialog
      setTimeout(() => {
        setShowDetailsModal(true) // Abrir o modal de detalhes
      }, 100)
      handleClose(true, createProductLink, false) // Fechar o modal de criação e passar o novo payment link
    }
  }

  const handleSubmitForm: SubmitHandler<
    CreatePaymentLinkFormInterface
  > = async (dataForm) => {
    // SC-11487: condição apontando para campo que vem depois, cópia em seção
    // sem o botão etc. A API recusa o mesmo, aqui o aviso vem antes de salvar.
    if (dataForm.hasExtraFields) {
      const [problem] = layoutProblems(extraFields, sections)
      if (problem) {
        toast.error(problem)
        return
      }
    }

    // Block if contract model has unresolved issues
    if (
      dataForm.hasContract &&
      dataForm.contractModelUuid &&
      hasContractIssues
    ) {
      if (contractValidation.blocked) {
        toast.error(contractValidation.blockReason)
      } else {
        toast.error(
          'Adicione todos os campos extras obrigatórios do contrato antes de salvar.'
        )
      }
      return
    }

    // Verificar especificamente os switches do cartão de crédito (novo formato)
    const creditCard = dataForm.payment_type_details?.credit_card
    // Trigger validation before proceeding
    const isFormValid = await trigger()

    if (!isFormValid) {
      toast.error('Corrija os erros no formulário antes de continuar')
      return
    }

    // Calcular valor total considerando quantidade mínima quando enable_customer_select_quantity está ativo
    const totalValue =
      dataForm.items?.reduce((sum: number, item: any) => {
        const price = Number(item.price) || 0
        let quantity =
          typeof item.quantity === 'string'
            ? parseFloat(item.quantity.replace(',', '.'))
            : Number(item.quantity) || 0

        // Se permite escolher quantidade e a quantidade é 0, usar a quantidade mínima para validação
        if (item.enable_customer_select_quantity && quantity === 0) {
          const minQty = Number(item.minimum_quantity) || 1
          quantity = minQty > 0 ? minQty : 1
        }

        return sum + price * quantity
      }, 0) || 0

    if (totalValue < 0) {
      toast.error('O valor total não pode ser negativo')
      return
    }

    // Validar quantidade mínima dos itens
    const itemWithInvalidQuantity = dataForm.items?.find((item: any) => {
      if (!item.enable_customer_select_quantity) return false
      const quantity =
        typeof item.quantity === 'string'
          ? parseFloat(item.quantity.replace(',', '.'))
          : Number(item.quantity) || 0
      const minQuantity = Number(item.minimum_quantity) || 0
      return quantity < minQuantity
    })

    if (itemWithInvalidQuantity) {
      toast.error(
        'A quantidade do item não pode ser menor que a quantidade mínima.'
      )
      return
    }

    if (
      creditCard?.enabled &&
      creditCard?.max_installments &&
      creditCard.max_installments > 1
    ) {
      const hasAtLeastOneSwitch = !!(
        creditCard.credit_card?.enabled ||
        creditCard.credit_card_recurring?.enabled ||
        creditCard.credit_card_installments?.enabled
      )

      if (!hasAtLeastOneSwitch) {
        const isRecurrence = false // Payment links são sempre únicos
        const hasInstallments = true // Já estamos dentro da condição creditCard.installments > 1
        const errorMessage = getCreditCardPaymentOptionsText(
          isRecurrence,
          hasInstallments
        )

        toast.error(errorMessage)
        paymentsTypeRef.current?.scrollToCreditCard()
        return
      }
    }

    // Abrir dialog de confirmação em vez de submeter diretamente
    setConfirmDialog({
      open: true,
      dataForm
    })
  }

  // Função que realmente submete o formulário após confirmação
  const handleConfirmedSubmit = async (
    dataForm: CreatePaymentLinkFormInterface
  ) => {
    // ✅ Validação de descontos seguindo o padrão do BodyModalNewChargeOrRecurrence
    let hasDiscountErrors = false
    const { errors } = formState

    if (dataForm.discountPolicy && dataForm.discounts) {
      dataForm.discounts.forEach((discount, index) => {
        // Verificar erros nos campos de desconto
        const amountError = errors.discounts?.[index]?.amount
        const dateError = errors.discounts?.[index]?.date
        const daysError = errors.discounts?.[index]?.days

        if (amountError || dateError || daysError) {
          hasDiscountErrors = true
        }

        // Verificar campos obrigatórios
        if (!discount.amount || discount.amount.trim() === '') {
          hasDiscountErrors = true
        }
      })
    }

    if (hasDiscountErrors) {
      setConfirmDialog({ open: false, dataForm: null })
      // SC-10012: antes fechava sem avisar e o lojista não sabia o que faltava
      toast.error('Revise os descontos: informe o valor e a data de cada um.')
      return
    }

    setLoading(true)
    // SC-10012: qualquer exceção deixava o botão carregando e nada aparecia
    try {
      // Usar formatSharedChargeData para os campos compartilhados
      const sharedData = formatSharedChargeData({
        dataForm: dataForm as any,
        isUnique,
        isCharge,
        isEdit: isEdit && !isDuplication,
        isProductOrLink: true,
        isLink: true
      })

      // Mapear document_type do frontend para o backend
      const documentTypeMap: Record<string, 'cpf' | 'cnpj' | 'both'> = {
        PF: 'cpf',
        PJ: 'cnpj',
        BOTH: 'both'
      }

      // Preparar link_fields para contatos
      const link_fields: Record<string, any> = {}
      if (
        dataForm.customerType === 'new' &&
        dataForm.contacts_enabled &&
        dataForm.contacts &&
        dataForm.contacts.length > 0
      ) {
        link_fields.contacts = {
          enabled: true,
          contacts_fields: dataForm.contacts.map((contact) => ({
            contact_type: contact.type,
            required: contact.required || false
          }))
        }
      }
      // SC-11487: seções do formulário do checkout
      link_fields.sections =
        dataForm.hasExtraFields && extraFields.length > 0
          ? sections.map((section) => ({
              ...section,
              title: section.title.trim()
            }))
          : []

      const data = {
        product_link: {
          // Campos específicos do payment link
          title: dataForm.title || null,
          message: dataForm.hasCustomMessage
            ? dataForm.customMessage || null
            : null,
          custom_output_url: dataForm.hasCustomUrl
            ? dataForm.customUrl || null
            : null,
          send_customer_welcome: dataForm.sendWelcomeEmail || false,
          simplified_checkout: resolveSimplifiedCheckoutPayload(
            dataForm.customerType,
            dataForm.simplifiedCheckout
          ),
          enable_customer_select_quantity:
            dataForm.canChangeCheckoutQuantity || false,
          quantity_default: dataForm.defaultQuantity || 1,
          customer_groups_uuids:
            dataForm.hasCustomerGroups && dataForm.groupsValue
              ? dataForm.groupsValue.map(({ value }) => value).join(';')
              : '',

          // Person Type (Tipo de Cliente) - apenas para novos clientes
          ...(dataForm.customerType === 'new' && {
            document_type: documentTypeMap[dataForm.link_type || 'BOTH']
          }),

          // Contacts (link_fields). Vai sempre, mesmo vazio: sem a chave o back
          // mantém os contatos antigos e o checkout segue pedindo.
          link_fields,

          // Existing document behavior (Comportamento no caso de cliente existente)
          ...(dataForm.customerType === 'new' && {
            existing_document_behavior: (() => {
              // SC-10380: default/fallback é update_customer (2)
              const behavior = dataForm.existing_document_behavior ?? 2
              if (behavior === 0) return 'skip_customer'
              if (behavior === 1) return 'create_customer'
              if (behavior === 2) return 'update_customer'
              return 'update_customer'
            })()
          }),

          // Campos compartilhados do formatSharedChargeData
          ...sharedData,
          charge_type: dataForm.chargeType === 0 ? 'single' : 'recurrent',

          // E-mails para notificar. Vai sempre, mesmo vazio: sem a chave o back
          // mantém os e-mails antigos.
          emails_to_notify: (dataForm.emails_to_notify || [])
            .filter((item) => item.email && item.email.trim() !== '')
            .map((item) => item.email.trim())
            .join(';'),
          consider_active_on_registration:
            !dataForm.consider_active_on_registration || false,
          cancel_if_first_billing_unpaid:
            dataForm.cancel_if_first_billing_unpaid || false,

          // Cupons
          coupons_enabled: couponsEnabled,

          // Contrato automático
          contract_model_uuid:
            dataForm.hasContract && dataForm.contractModelUuid
              ? dataForm.contractModelUuid
              : null,
          ...(dataForm.hasContract &&
            dataForm.contractModelUuid && {
              contract_billing_behavior:
                dataForm.contractBillingBehavior || 'contract_and_billing'
            })
        },

        // customer_uuid no nível raiz se cliente existente
        ...(dataForm.customerType === 'existing' && {
          customer_uuid: dataForm.customerUuid
        }),

        ...(dataForm.customerType === 'new' && {
          customer_uuid: null
        }),

        // Cupons associados
        coupon_uuids: couponsEnabled
          ? associatedCoupons.map((c) => c.uuid)
          : [],

        // custom_field_configs seguindo o padrão da API V3
        ...(dataForm.hasExtraFields &&
          Array.isArray(extraFields) &&
          extraFields.length > 0 && {
            custom_field_configs: extraFields.map((field, index) => ({
              custom_field_uuid: field.uuid,
              required: field.required || false,
              position: index + 1,
              display_settings: displaySettingsPayload(field)
            }))
          })
      }

      const response = await DataService({
        data,
        type: isEdit && !isDuplication ? 'PATCH' : 'POST',
        url:
          isEdit && !isDuplication
            ? `/v3/product_links/${paymentLink?.uuid}`
            : '/v3/product_links'
      })

      const errorMessage =
        response.data.detail_error || response.detail_error || response.message

      if (response.error) {
        toast.error(() => formatedMessageError(errorMessage))
      }

      if (!response.error) {
        setCreateProductLink(response.data.record)
        // Fechar o dialog de confirmação e abrir o dialog de sucesso
        setConfirmDialog({ open: false, dataForm: null })
        setDialogOpen(true)
      } else {
        // Em caso de erro, também fechar o dialog de confirmação
        setConfirmDialog({ open: false, dataForm: null })
      }
    } catch (error) {
      setConfirmDialog({ open: false, dataForm: null })
      toast.error('Não foi possível salvar o link. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const productUuidFromUrl = query?.plano

    if (!productUuidFromUrl) return

    setValue('productUuid', productUuidFromUrl as string)
  }, [query])

  // useEffect para aplicar validação em tempo real nos descontos seguindo o padrão do BodyModalNewChargeOrRecurrence
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      // Só processar mudanças relacionadas aos descontos
      if (!name || !name.startsWith('discounts.')) return

      const discounts = value.discounts || []
      const discountPolicy = value.discountPolicy

      if (!discountPolicy || discounts.length === 0) return

      // Extrair índice do campo que mudou
      const match = name.match(/^discounts\.(\d+)\./)
      if (!match) return

      const index = parseInt(match[1])
      const discount = discounts[index]

      if (!discount) return

      // Revalidar o desconto específico que mudou
      setTimeout(() => {
        const items = value.items || []
        const totalAmount = items.reduce(
          (acc: number, item: any) => acc + (item.total || 0),
          0
        )

        // Validar campo amount se foi alterado
        if (
          name.includes('.amount') &&
          discount.amount &&
          discount.amount.trim() !== ''
        ) {
          const isPercent = discount.type === '0'
          let validationResult: string | true = true

          if (isPercent) {
            const numericValue = Number(discount.amount)
            if (isNaN(numericValue) || numericValue <= 0) {
              validationResult = 'A porcentagem deve ser maior que 0%'
            } else if (numericValue > 90) {
              validationResult =
                'A porcentagem do desconto deve ser menor ou igual a 90%.'
            }
          } else {
            const numericValue = Number(discount.amount)
            if (isNaN(numericValue) || numericValue <= 0) {
              validationResult = 'O valor deve ser maior que 0.'
            } else if (totalAmount > 0) {
              if (numericValue > totalAmount) {
                validationResult =
                  'O valor do desconto deve ser menor que o da fatura.'
              } else if (numericValue > totalAmount * 0.9) {
                validationResult =
                  'O valor do desconto deve ser menor ou igual a 90% do valor total.'
              }
            }
          }

          if (validationResult !== true) {
            setError(`discounts.${index}.amount`, {
              type: 'validate',
              message: validationResult
            })
          } else {
            clearErrors(`discounts.${index}.amount`)
          }
        }

        // Validar campo date se foi alterado (cobrança única)
        if (name.includes('.date') && value.chargeType === 0 && discount.date) {
          const dueDate = value.dueDate
          let validationResult: string | true = true

          const discountDate = new Date(discount.date)
          const today = new Date()
          const dueDateObj = new Date(dueDate || new Date())

          if (discountDate < today) {
            validationResult = 'A data deve ser maior ou igual a hoje.'
          } else if (discountDate > dueDateObj) {
            validationResult = 'A data deve ser anterior ao vencimento.'
          } else {
            // Verificar duplicidade
            const hasDuplicate = discounts.some(
              (otherDiscount: any, otherIndex: number) => {
                if (otherIndex === index || !otherDiscount.date) return false
                return (
                  new Date(otherDiscount.date).toDateString() ===
                  discountDate.toDateString()
                )
              }
            )

            if (hasDuplicate) {
              validationResult =
                'O dia do desconto deve ser diferente do dia já selecionado.'
            }
          }

          if (validationResult !== true) {
            setError(`discounts.${index}.date`, {
              type: 'validate',
              message: validationResult
            })
          } else {
            clearErrors(`discounts.${index}.date`)
          }
        }

        // Validar campo days se foi alterado (recorrência)
        if (name.includes('.days') && value.chargeType !== 0 && discount.days) {
          let validationResult: string | true = true

          // Verificar duplicidade
          const hasDuplicate = discounts.some(
            (otherDiscount: any, otherIndex: number) => {
              if (otherIndex === index || !otherDiscount.days) return false
              return otherDiscount.days === discount.days
            }
          )

          if (hasDuplicate) {
            validationResult =
              'O dia do desconto deve ser diferente do dia já selecionado.'
          }

          if (validationResult !== true) {
            setError(`discounts.${index}.days`, {
              type: 'validate',
              message: validationResult
            })
          } else {
            clearErrors(`discounts.${index}.days`)
          }
        }
      }, 0)
    })

    return () => subscription.unsubscribe()
  }, [watch, setError, clearErrors])

  useEffect(() => {
    const fetchProductData = async () => {
      if (!productUuid || product) {
        return
      }

      setLoadingProduct(true)
      try {
        const response = await DataService({
          url: `/v1/products/${productUuid}`,
          type: 'GET'
        })

        if (response.status === 200) {
          const convertedProduct = convertProductPropertiesToCamelCase(
            response.data.record
          )
          setFetchedProduct(convertedProduct)
          setValue('productUuid', productUuid)
        }
      } catch (error) {
        console.error('Erro ao buscar dados do produto:', error)
      } finally {
        setLoadingProduct(false)
      }
    }

    if (isOpen && productUuid) {
      fetchProductData()
    }
  }, [isOpen, productUuid, product, setValue])

  useEffect(() => {
    if (currentProduct && isOpen && !isEdit && !loadingProduct) {
      setTimeout(() => {
        setValue('productUuid', currentProduct.uuid)

        setValue('title', currentProduct.name || '')
        setValue('description', currentProduct.description || '')

        setValue('items.0.product', {
          label: currentProduct.name,
          value: currentProduct.uuid,
          total_cycles: currentProduct.totalCycles
        })
        setValue('items.0.price', Number(currentProduct.amount) || 0)
        setValue('items.0.quantity', 1)
        setValue('items.0.total', Number(currentProduct.amount) || 0)
        setValue('items.0.description', '')

        if (
          currentProduct.chargeType === 'single' ||
          currentProduct.totalCycles === 1
        ) {
          setIsUnique(true)
          setValue('chargeType', 0)
          setValue('totalCycles', 1)
        } else {
          setIsUnique(false)
          setValue('chargeType', 1)

          if (currentProduct.totalCycles === 0) {
            setValue('totalCycles', 0)
          } else if (currentProduct.totalCycles > 12) {
            setValue('totalCycles', 13)
            setValue('totalCyclesCustom', String(currentProduct.totalCycles))
          } else {
            setValue('totalCycles', currentProduct.totalCycles)
          }
        }

        // O par canônico acompanha a frequência: presets o backend re-deriva, mas
        // "Personalizada" só sobrevive se o intervalo do produto vier junto.
        setValue(
          'frequency',
          FREQUENCY_NAME_TO_ENUM[currentProduct.frequency] ?? 1
        )
        const [productUnit, productCount] = presetToPair(
          currentProduct.frequency,
          currentProduct.frequency_type,
          currentProduct.frequency_number
        )
        setValue('frequency_type', productUnit)
        setValue('frequency_number', productCount)

        if (currentProduct.dueDay) {
          setValue('dueDay', String(currentProduct.dueDay))
        }

        setValue('dueType', currentProduct.dueType || 'immediate_charge')

        setValue('proratedCharge', currentProduct.proratedCharge || false)

        if (currentProduct.interestPolicy === 'custom') {
          setValue('interestPolicy', true)
          setValue('interestFee', currentProduct.interestFee || '1.0')
          setValue('interestFine', currentProduct.interestFine || '2.0')
        } else {
          setValue('interestPolicy', false)
        }

        if (
          currentProduct.nfsePolicy &&
          currentProduct.nfsePolicy !== 'no_nfse'
        ) {
          setValue('interestInvoice', true)
          const isCustomPolicy =
            currentProduct.nfsePolicy === 'on_before_due_date' ||
            currentProduct.nfsePolicy === 'on_after_due_date'

          setValue(
            'issue_when',
            isCustomPolicy ? 'on_custom_issued' : currentProduct.nfsePolicy
          )

          if (isCustomPolicy) {
            setValue(
              'due_date_period',
              currentProduct.nfsePolicy as
                | 'on_before_due_date'
                | 'on_after_due_date'
            )
            if (currentProduct.nfseScheduled?.days) {
              setValue('days', currentProduct.nfseScheduled.days.toString())
            }
          }

          // Dia específico do mês (novo modelo)
          if (
            (currentProduct as any)?.nfseScheduled?.configuration_type === 1 ||
            (currentProduct as any)?.nfseScheduled?.configuration_type ===
              'specific_day_of_month' ||
            (currentProduct as any)?.nfseScheduled?.issue_mode ===
              'specific_day'
          ) {
            ;(setValue as any)('issue_mode', 'specific_day')
            const sched: any = (currentProduct as any).nfseScheduled
            ;(setValue as any)(
              'specific_day',
              sched?.day_of_month || sched?.specific_day || 1
            )
            const rel = sched?.due_date_relation as string | undefined
            const offset: '-2' | '-1' | '0' | '1' | '2' | undefined =
              rel === 'two_months_before'
                ? '-2'
                : rel === 'month_before'
                ? '-1'
                : rel === 'same_month'
                ? '0'
                : rel === 'month_after'
                ? '1'
                : rel === 'two_months_after'
                ? '2'
                : undefined
            ;(setValue as any)(
              'specific_month_offset',
              offset ?? sched?.specific_month_offset ?? ''
            )
          }

          if (
            currentProduct.nfseScheduled?.amount_type === 'amount_custom' &&
            currentProduct.nfseScheduled?.amount
          ) {
            setValue(
              'amountCustom',
              currentProduct.nfseScheduled.amount.toString()
            )
          }
        } else {
          setValue('interestInvoice', false)
        }

        setHasInitialProduct(true)
      }, 100)
    }
  }, [currentProduct, isOpen, isEdit, loadingProduct, setValue])

  useEffect(() => {
    const portalUrl = getPortalBaseUrl()
    // Remove https:// prefix for display
    const displayUrl = portalUrl.replace('https://', 'www.')
    setValue('customDomain', displayUrl)
  }, [])

  // useEffect para resetar o formulário quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      // Limpar estados de dialog e detalhes quando o modal abrir
      setShowDetailsModal(false)
      setDialogOpen(false)
      setHasInitialProduct(false)
      getContractModels()

      if (isEdit && paymentLink) {
        // Mapeia os dados do payment link para o formato do formulário, passando as funções de set
        const formValues = createFormValuesFromPaymentLinkV3(
          paymentLink as any,
          availableTags
        )

        // Reseta o formulário com os valores mapeados
        reset(formValues, {
          keepDefaultValues: false,
          keepDirty: false,
          keepTouched: false
        })

        // Define o estado isUnique baseado no chargeType do formulário
        if (formValues.chargeType === 0) {
          setIsUnique(true)
        } else {
          setIsUnique(false)
        }

        // Popula estados auxiliares se existirem nos dados
        let parsedExtraFields: typeof extraFields = []
        if (
          (paymentLink as any).custom_field_configs?.length > 0 ||
          (paymentLink as any).extra_fields?.length > 0
        ) {
          setValue('hasExtraFields', true)
          parsedExtraFields = createExtraFieldsInitialValue(paymentLink as any)
          setExtraFields(parsedExtraFields)
          setSections(
            normalizeSections((paymentLink as any).link_fields?.sections)
          )
        } else {
          setValue('hasExtraFields', false)
          setExtraFields(extraFieldsDefaultValue)
          setSections([])
        }

        setTimeout(() => {
          setSharedFormData(
            {
              // Dados de desconto
              discounts: (paymentLink as any).discounts,
              discountPolicy: (paymentLink as any).discount_policy,
              issueDays: (paymentLink as any).issue_days,
              // Dados de juros
              interestPolicy: (paymentLink as any).interest_policy,
              interestFee: (paymentLink as any).interest_fee,
              interestFine: (paymentLink as any).interest_fine,

              // Dados de negativação - estrutura específica para links
              negativation: (paymentLink as any)
                .billing_no_payment_report_scheduled,

              // Dados de periodicidade
              frequency: (paymentLink as any).frequency,
              frequency_type: (paymentLink as any).frequency_type,
              frequency_number: (paymentLink as any).frequency_number,
              totalCycles: (paymentLink as any).total_cycles,
              dueDay: (paymentLink as any).due_day,

              // Dados de expiração
              expirationField: (paymentLink as any).limit_days_for_payment,
              limitDaysForPayment: (paymentLink as any).limit_days_for_payment,

              // Dados de pagamento - novo formato
              paymentTypeDetails: (paymentLink as any).payment_type_details,

              // Dados de nota fiscal
              nfseScheduled: (paymentLink as any).nfse_scheduled,
              nfsePolicy: (paymentLink as any).nfse_policy,
              nfseIssuerUuid: (paymentLink as any).nfse_issuer_uuid,

              // Dados de bonificação
              hasBonus:
                (paymentLink as any).bonus_policy !== 'no_bonus' &&
                !!(paymentLink as any).bonus,
              bonusSettings:
                (paymentLink as any).bonus_policy !== 'no_bonus' &&
                (paymentLink as any).bonus
                  ? {
                      // Manda o total_cycles CRU. Quem traduz para o sentinela 13
                      // ("Outros") e preenche o cyclesCountCustom é o
                      // setSharedFormData — converter aqui também faria a conversão
                      // rodar duas vezes: ele receberia 13, o teste `> 12` daria
                      // falso e o campo do número personalizado ficaria vazio.
                      cyclesCount: (paymentLink as any).bonus.total_cycles || 1,
                      startCycle: (paymentLink as any).bonus.start_cycle || 1,
                      type:
                        (paymentLink as any).bonus_policy === 'fixed'
                          ? 'value'
                          : 'percentage',
                      amount: (paymentLink as any).bonus?.amount
                        ? (paymentLink as any).bonus_policy === 'fixed'
                          ? formatCents(
                              (paymentLink as any).bonus.amount.toString()
                            )
                          : (paymentLink as any).bonus.amount.toString()
                        : ''
                    }
                  : undefined,

              // Dados de identificação interna (metadata)
              metadata: (paymentLink as any).metadata,

              // Dados de items
              items: (paymentLink as any).billing_items,

              // Campos específicos de links (TypesCharges)
              chargeType: (paymentLink as any).charge_type,
              dueType: (paymentLink as any).due_type,
              proratedCharge: (paymentLink as any).prorated_charge,

              // Dados de reajuste agendado
              adjustmentScheduled: (paymentLink as any).adjustment_scheduled,

              // Dados de descrição da cobrança
              billingDescription: (paymentLink as any).billing_description
            },
            setValue,
            replaceDiscounts, // usar replaceDiscounts para os descontos
            formValues.chargeType !== 0, // isRecurrence
            undefined,
            true // isProductOrLink
          )
        }, 50)
        // Populate coupon fields from payment link
        setCouponsEnabled(!!(paymentLink as any).coupons_enabled)
        setAssociatedCoupons((paymentLink as any).coupons || [])

        // Populate contract fields from payment link
        if ((paymentLink as any).contract_model_uuid) {
          setValue('hasContract', true)
          setValue(
            'contractModelUuid',
            (paymentLink as any).contract_model_uuid
          )
          setValue(
            'contractBillingBehavior',
            (paymentLink as any).contract_billing_behavior ||
              'contract_and_billing'
          )
          // Validate contract model after form values are set
          // Pass parsedExtraFields directly to avoid stale closure issue
          setTimeout(() => {
            validateContractModel(
              (paymentLink as any).contract_model_uuid,
              parsedExtraFields
            )
          }, 300)
        }
      } else if (!isEdit) {
        // Para criação, resetar o formulário com valores iniciais limpos
        const initialEmails = user?.email ? [{ email: user.email }] : []

        reset({
          ...initialFormValues,
          emails_to_notify: initialEmails
        })
        setExtraFields(extraFieldsDefaultValue)
        setSections([])
        // ✅ Inicializar vazio - o OptionsAdditionals gerenciará a adição do primeiro desconto
        replaceDiscounts([])
        setIsUnique(true) // Padrão para cobrança única
        setCouponsEnabled(false)
        setAssociatedCoupons([])
        setHasInitialProduct(false)
      }
    }
  }, [
    isOpen,
    isEdit,
    paymentLink,
    product,
    productUuid,
    reset,
    setValue,
    user?.email,
    getContractModels
  ])

  // Converter tags quando paymentLink ou availableTags mudarem
  useEffect(() => {
    if (paymentLink && availableTags.length > 0) {
      const formDataWithTags = prepareFormDataWithTags(
        paymentLink,
        availableTags
      )

      // Se há tags para converter, atualizar o formulário
      if (formDataWithTags.tags && formDataWithTags.tags.length > 0) {
        setValue('tags', formDataWithTags.tags)
      }
    }
  }, [paymentLink, availableTags, setValue])

  const formTitle = isDuplication
    ? 'Duplicar Link de Contratação'
    : isEdit
    ? 'Editar Link de Contratação'
    : 'Novo Link de Contratação'

  return (
    <>
      <Modal
        handleClose={handleCloseModal}
        open={isOpen}
        width="100%"
        maxWidth="85vw"
        maxHeight="90vh"
        height="auto"
        disablePortal={false}
      >
        <ModalBody>
          <ModalHeader onClose={handleCloseModal}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              mr={2}
            >
              <Stack direction="row" alignItems="center" gap={2}>
                <IconContainer icon="faCreditCard" />
                <Typography variant="smBold">{formTitle}</Typography>
              </Stack>
              <Button
                onClick={() => setIsOpenPreview(true)}
                variantButton="neutral"
                size="small"
              >
                Visualizar modelo
              </Button>
            </Stack>
          </ModalHeader>
          <ModalContent>
            {companySelector}
            {loadingProduct ? (
              <Skeleton />
            ) : (
              <CreatePaymentLinkForm
                control={control}
                watch={watch}
                setValue={setValue}
                trigger={trigger}
                setError={setError}
                register={register}
                formState={formState}
                customerGroups={customerGroups}
                extraFields={extraFields}
                setExtraFields={setExtraFields}
                sections={sections}
                setSections={setSections}
                isUnique={isUnique}
                setIsUnique={setIsUnique}
                isEdit={isEdit}
                hasInitialProduct={hasInitialProduct}
                onProductRemoved={handleProductRemoved}
                clearErrors={clearErrors}
                isCharge={isCharge}
                setIsCharge={setIsCharge}
                externalReplaceDiscounts={replaceDiscounts}
                paymentsTypeRef={paymentsTypeRef}
                emailsFields={emailsFields}
                appendEmail={appendEmail}
                removeEmail={removeEmail}
                contactsFields={contactsFields}
                appendContact={appendContact}
                removeContact={removeContact}
                contractModels={contractModels}
                contractValidation={contractValidation}
                onContractModelChange={validateContractModel}
                onAddContractCustomField={handleAddContractCustomField}
                onAddAllContractCustomFields={handleAddAllContractCustomFields}
                couponsEnabled={couponsEnabled}
                onCouponsEnabledChange={setCouponsEnabled}
                associatedCoupons={associatedCoupons}
                onCouponsChange={setAssociatedCoupons}
                couponsTotalAmount={(watch('items') || []).reduce(
                  (sum: number, item: any) =>
                    sum +
                    (Number(item.price) || 0) * (Number(item.quantity) || 1),
                  0
                )}
              />
            )}
          </ModalContent>
          <ModalFooter>
            <Stack direction="row" justifyContent="flex-end" width="100%">
              <Stack direction="row" alignItems="center" gap={2}>
                <TagsPopover control={control} name="tags" maxTags={10} />

                <Button
                  variantButton="primary"
                  onClick={(e) => {
                    handleSubmit(handleSubmitForm, toastFirstError)(e)
                  }}
                  loading={loading && !confirmDialog.open}
                  disabled={loading}
                >
                  {isDuplication ? 'Duplicar' : isEdit ? 'Atualizar' : 'Salvar'}
                </Button>
              </Stack>
            </Stack>
          </ModalFooter>
        </ModalBody>
      </Modal>
      <Dialog
        icon={'checkCircle'}
        iconColor={theme.palette.primary.main}
        title={
          isDuplication
            ? 'Link duplicado!'
            : isEdit
            ? 'Link atualizado!'
            : 'Link criado!'
        }
        description={
          isDuplication
            ? 'Seu Link foi duplicado com sucesso! Agora você pode acessá-lo ou ver os detalhes.'
            : isEdit
            ? 'Volte para a listagem ou veja seu Link agora mesmo!'
            : 'Agora você já pode fazer suas vendas muito mais rápido!'
        }
        setIsOpenDialog={handleCloseDialog}
        isOpenDialog={dialogOpen}
      >
        <Stack direction="row" spacing={2} width="100%">
          <Button
            autoFocus
            disabled={loading}
            onClick={openDetailsModal}
            fullWidth
            variantButton="neutral"
          >
            Ver Detalhes
          </Button>
          <Button
            disabled={loading}
            loading={loading}
            onClick={openPaymentLink}
            fullWidth
            variantButton="primary"
          >
            Acessar Link
          </Button>
        </Stack>
      </Dialog>
      <ModalPaymentLinkPreview
        isOpen={isOpenPreview}
        handleClose={() => setIsOpenPreview(false)}
        watch={watch}
        extraFields={extraFields}
        paymentLink={paymentLink}
        title={watch('title') || 'Visualizar modelo'}
      />
      {isOpenProductModal && (
        <Modal
          handleClose={handleOpenNewProductModal}
          open={isOpenProductModal}
          width="100%"
          maxWidth="85vw"
          maxHeight="95vh"
          height="100%"
        >
          <>
            <FormProducts
              setIsOpenDialog={handleOpenNewProductModal}
              setValueFromPaymentLinkCreation={setValue}
            />
          </>
        </Modal>
      )}
      {/* Dialog de Confirmação */}
      <Dialog
        icon="faCreditCard"
        title={
          isDuplication
            ? 'Duplicar Link de Contratação'
            : isEdit
            ? 'Editar Link de Contratação'
            : 'Novo Link de Contratação'
        }
        description={
          isDuplication
            ? 'Confirma duplicar esse Link?'
            : isEdit
            ? 'Confirma salvar as alterações?'
            : 'Confirma criar esse Link?'
        }
        setIsOpenDialog={() =>
          setConfirmDialog({ open: false, dataForm: null })
        }
        isOpenDialog={confirmDialog.open}
        cancelButton
      >
        <Button
          id="confirm-create-link"
          fullWidth
          onClick={() => {
            if (confirmDialog.dataForm) {
              handleConfirmedSubmit(confirmDialog.dataForm)
              // Não fechar o dialog aqui - ele será fechado após o submit
            }
          }}
          disabled={loading}
          loading={loading}
        >
          {isDuplication ? 'Duplicar' : isEdit ? 'Atualizar' : 'Criar'}
        </Button>
      </Dialog>

      {/* Modal de Detalhes do Payment Link Criado */}
      {createProductLink && showDetailsModal && (
        <ModalPaymentLinkDetail
          isOpenModalPaymentDetail={showDetailsModal}
          handleCloseModalDetailPayment={() => {
            setShowDetailsModal(false)
            setCreateProductLink(null) // Limpar após fechar os detalhes
          }}
          productLink={createProductLink}
        />
      )}
    </>
  )
}

export default ModalNewPaymentLink
